'use client'

import { ColorPalette, DefaultColorPalette } from '@/lib/utils'
import log from 'loglevel'
import React, { PropsWithChildren } from 'react'
import { createContext, useEffect, useState } from 'react'
import tinycolor from 'tinycolor2'

log.enableAll()
const getInitialColorMode = (): ColorMode => {
  const persistedColorPreference = window.localStorage.getItem('color-mode') as ColorMode
  const hasPersistedPreference = typeof persistedColorPreference === 'string'

  if (hasPersistedPreference) {
    return persistedColorPreference
  }

  const mql = window.matchMedia('(prefers-color-scheme: dark)')
  const hasMediaQueryPreference = typeof mql.matches === 'boolean'
  if (hasMediaQueryPreference) {
    return mql.matches ? 'dark' : 'light'
  }

  return 'light' as ColorMode
}
type ColorMode = 'light' | 'dark'

type ThemeContext = {
  colorMode?: ColorMode
  setColorMode?: (mode: ColorMode) => void
  setPalette?: (mode: ColorPalette) => void
} & ColorPalette

export const ThemeContext = createContext<ThemeContext>(undefined as any)
const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [colorMode, rawSetColorMode] = useState<ColorMode>()
  const [palette, setRawPalette] = useState({})

  useEffect(() => {
    const root = window.document.documentElement
    rawSetColorMode(getInitialColorMode)

    const colors: ColorPalette = Object.keys(DefaultColorPalette as ColorPalette).reduce((current, key) => {
      const val = root.style.getPropertyValue(`--color-${key}`)
      const color = tinycolor(val)
      if (color.isValid()) {
        current[key] = color.toString()
      } else {
        current[key] = (DefaultColorPalette as any)[key]
      }
      return current
    }, {} as any)
    setPalette(colors)
  }, [])

  const setColorMode = (value: 'light' | 'dark') => {
    rawSetColorMode(value)
    window.localStorage.setItem('color-mode', value)
  }
  const toTailwindRgbString = (color: tinycolor.Instance) => {
    return `${color.toRgb().r} ${color.toRgb().g} ${color.toRgb().b}`
  }
  const setPalette = (colorPalette: ColorPalette) => {
    const root = window.document.documentElement
    const colors: ColorPalette = Object.keys(colorPalette as ColorPalette).reduce((current, key) => {
      const pColor = (colorPalette as any)[key]
      const color = tinycolor(pColor)
      root.style.setProperty(`--color-${key}`, toTailwindRgbString(color))
      root.style.setProperty(`--color-${key}-light`, toTailwindRgbString(tinycolor(pColor).lighten()))
      root.style.setProperty(`--color-${key}-lighter`, toTailwindRgbString(tinycolor(pColor).lighten(20)))
      root.style.setProperty(`--color-${key}-dark`, toTailwindRgbString(tinycolor(pColor).darken()))
      root.style.setProperty(`--color-${key}-darker`, toTailwindRgbString(tinycolor(pColor).darken(20)))
      root.style.setProperty(`--color-${key}-inverse`, toTailwindRgbString(color.complement()))
      current[key] = color.toRgbString()
      return current
    }, {} as any)
    setRawPalette((c) => ({ ...c, ...colors }))
  }
  return <ThemeContext.Provider value={{ colorMode, setColorMode, setPalette }}>{children}</ThemeContext.Provider>
}

export default ThemeProvider
